import React from 'react';
import propTypes from 'prop-types';
import superagent from 'superagent';
import { FormattedMessage, injectIntl, defineMessages } from 'react-intl';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Container, Popup } from 'semantic-ui-react';
import { useHistory } from 'react-router-dom';
import './SignUpForm.scss';

function ErrorField({ children }) {
  return <span className="errorField">{children}</span>;
}

const messages = defineMessages({
  validationNoSpaces: {
    id: 'No spaces are allowed.',
    defaultMessage: 'No spaces are allowed.',
  },
  validationAlphanumOnly: {
    id: 'Only alpha-numeric characters are allowed.',
    defaultMessage: 'Only alpha-numeric characters are allowed.',
  },
  validationCloudnameRequired: {
    id: 'Please insert a cloudname.',
    defaultMessage: 'Please insert a cloudname.',
  },
});

// Schritt-spezifische Validierungen (als Factory, damit intl genutzt werden kann)
const makeStep1Schema = (intl) =>
  Yup.object().shape({
    cloudname: Yup.string()
      .strict()
      .trim(intl.formatMessage(messages.validationNoSpaces))
      .matches(
        /^[a-z0-9A-Z]+$/,
        intl.formatMessage(messages.validationAlphanumOnly),
      )
      .required(intl.formatMessage(messages.validationCloudnameRequired)),
    office: Yup.boolean(),
    talk: Yup.boolean(),
    ai: Yup.boolean(),
  });

const SignUpForm = ({ intl, source_input = 'head-signup' }) => {
  const history = useHistory();
  const language = intl.locale;

  return (
    <Container className="signupform" id="portknox-signup">
      <Formik
        enableReinitialize
        initialValues={{
          email: '',
          cloudname: '',
          office: false,
          talk: false,
          ai: false,
          success: false,
          cloudnameChecked: false,
        }}
        // Wizard: Validierung pro Schritt, daher hier kein globales Schema
        validationSchema={Yup.object()}
        onSubmit={async (
          values,
          { setSubmitting, setFieldError, setFieldValue },
        ) => {
          const { email, cloudname, office, talk, ai } = values;
          try {
            // Cloud-ID frei?
            await superagent
              .post('/++api++/@cloud-id-free')
              .set('accept', 'application/json')
              .send({ cloud_id: cloudname });

            const options = [
              office ? 'OPT0002' : null,
              talk ? 'OPT0010' : null,
              ai ? 'OPT0012' : null,
            ].filter(Boolean);

            await superagent
              .post('/++api++/@request-trial-cloud')
              .set('accept', 'application/json')
              .send({
                cloud_id: cloudname,
                email,
                language,
                source_input,
                options,
              });

            setFieldValue('success', true);
            setSubmitting(false);
            history.push(
              language === 'de' ? '/de/signup-success' : '/en/signup-success',
            );
          } catch (err) {
            // Fehlermeldung am sichtbaren Feld zeigen
            setFieldError('email', err.message);
            setSubmitting(false);
          }
        }}
      >
        {(formik) => <Wizard formik={formik} intl={intl} />}
      </Formik>
    </Container>
  );
};

function Wizard({ formik, intl }) {
  const [step, setStep] = React.useState(1);

  const next = async () => {
    const step1Schema = makeStep1Schema(intl);
    // Feld als "touched" markieren, damit ErrorMessage sofort angezeigt wird
    formik.setFieldTouched('cloudname', true, false);
    // Step 1 validieren
    let step1Errors = {};
    try {
      await step1Schema.validate(formik.values, { abortEarly: false });
    } catch (err) {
      step1Errors = err.inner.reduce(
        (acc, e) => ({ ...acc, [e.path]: e.message }),
        {},
      );
    }

    // Asynchron: cloud-id prüfen
    if (!step1Errors.cloudname) {
      try {
        await superagent
          .post('/++api++/@cloud-id-free')
          .set('accept', 'application/json')
          .send({ cloud_id: formik.values.cloudname });
        formik.setFieldValue('cloudnameChecked', true, false);
      } catch (e) {
        step1Errors.cloudname = e.message || 'Cloud name not available';
      }
    }

    if (Object.keys(step1Errors).length === 0) {
      setStep(2);
    } else {
      Object.entries(step1Errors).forEach(([k, v]) =>
        formik.setFieldError(k, v),
      );
    }
  };

  return (
    <Form onSubmit={formik.handleSubmit}>
      {step === 1 ? (
        <>
          <div className="fieldset">
            <label htmlFor={`input_cloudname`}>
              <b>
                <FormattedMessage
                  id="input_cloudname"
                  defaultMessage="Desired cloud name"
                />
              </b>
            </label>
            <div>
              <Field
                className={`input ${formik.touched.cloudname && formik.errors.cloudname ? 'inputError' : ''}`}
                id="input_cloudname"
                type="text"
                name="cloudname"
              />
            </div>
            <ErrorMessage component={ErrorField} name={`cloudname`} />
          </div>

          <div className="fieldset options">
            <label htmlFor={`cloud_options`}>
              <b>
                <FormattedMessage id="cloud_options" defaultMessage="Options" />
              </b>
              <Popup
                position="top center"
                content={
                  <FormattedMessage
                    id="tooltip_cloud_description"
                    defaultMessage="Options will be added shortly after cloud installation."
                  />
                }
                trigger={
                  <span
                    className="tooltipIcon"
                    role="button"
                    tabIndex={0}
                    aria-label="More information about options"
                  >
                    ?
                  </span>
                }
              />
            </label>

            <div>
              <div className="checkbox">
                <label>
                  <Field type="checkbox" name="office" />{' '}
                  <FormattedMessage
                    id="option_office"
                    defaultMessage="Nextcloud Office"
                  />
                </label>
              </div>
              <div className="checkbox">
                <label>
                  <Field type="checkbox" name="talk" />{' '}
                  <FormattedMessage
                    id="option_talk"
                    defaultMessage="Nextcloud Talk"
                  />
                </label>
              </div>
              <div className="checkbox">
                <label>
                  <Field type="checkbox" name="ai" disabled />{' '}
                  <FormattedMessage
                    id="option_ai"
                    defaultMessage="Nextcloud Assistant (AI)"
                  />
                  <Popup
                    position="top center"
                    content={
                      <FormattedMessage
                        id="tooltip_assistant_description"
                        defaultMessage="We are working on integration."
                      />
                    }
                    trigger={
                      <span
                        className="tooltipIcon"
                        role="button"
                        tabIndex={0}
                        aria-label="More information about Nextcloud Talk"
                      >
                        ?
                      </span>
                    }
                  />
                </label>
              </div>
            </div>
          </div>

          <div className="actions">
            <button type="button" className="button buttonLarge" onClick={next}>
              <FormattedMessage id="wizard_next" defaultMessage="Next" />
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="fieldset">
            <label htmlFor={`input_email`}>
              <FormattedMessage
                id="input_email"
                defaultMessage="Your email address"
              />
            </label>
            <div>
              <Field
                className={`input ${formik.touched.email && formik.errors.email ? 'inputError' : ''}`}
                id="input_email"
                type="email"
                name="email"
              />
            </div>
            <ErrorMessage component={ErrorField} name={`email`} />
            <p className="helpText">
              <FormattedMessage
                id="install_duration_hint"
                defaultMessage="After activation via the confirmation email, installation may take up to 10 minutes. Once your cloud is ready, we will send you an email with your login details."
              />
            </p>
          </div>

          <div className="actions">
            <button type="submit" className="button buttonLarge">
              <FormattedMessage id="wizard_submit" defaultMessage="Sign up" />
            </button>
          </div>
        </>
      )}
    </Form>
  );
}

SignUpForm.propTypes = {
  intl: propTypes.object,
};

export default injectIntl(SignUpForm);
